"use client"

import * as React from "react"

import { ChevronDownIcon, PlayIcon, Trash2Icon } from "lucide-react"

import {
  Badge,
  Button,
  ConfirmActionDialog,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useActiveBranchTimezone } from "@/features/auth/hooks/use-active-branch-timezone"
import { resolveDerivationRange } from "@/features/finance/lib/transfer-derivation"
import { PeriodPreset } from "@/shared/lib/dates/period-range"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"
import { TransferDerivationMode, type SavedTransferCalculation } from "@/features/finance/model/finance-types"
import {
  useDeleteSavedTransferCalculationMutation,
  useSavedTransferCalculationsQuery,
} from "@/features/finance/services/finance-query"
import { useApiErrorToast } from "@/shared/hooks/use-api-error"

/**
 * Saved calculations, above the transfers list.
 *
 * A church remits on a schedule, so the same calculation runs every week forever. These cards are that
 * routine made one click: pick the one you mean, and the next screen already has the period resolved against
 * today, the amount calculated, and the destination and fund filled in.
 *
 * COLLAPSED by default, and absent entirely when there are none — a treasurer who has saved nothing should
 * not be given an empty box to wonder about above their transfers.
 */
export function SavedCalculationsPanel({
  workspaceId,
  branchId,
  canEdit,
  onApply,
}: {
  workspaceId: string | null
  branchId: string | null
  /** Named edit decision propagated from the central finance authorization check. */
  canEdit: boolean
  /** Start a transfer from this calculation. */
  onApply: (calculation: SavedTransferCalculation) => void
}) {
  const t = useTranslations("finance.transferRequests.saved")
  const toastError = useApiErrorToast("finance")
  const [open, setOpen] = React.useState(false)
  const [pendingDelete, setPendingDelete] = React.useState<SavedTransferCalculation | null>(null)
  const timeZone = useActiveBranchTimezone()

  const query = useSavedTransferCalculationsQuery(
    workspaceId && branchId ? { workspaceId, branchId } : null,
  )
  const remove = useDeleteSavedTransferCalculationMutation()

  const items = query.data ?? []

  /**
   * A preset's dates as of now. Memoised per preset because several cards usually share one, and resolving is
   * pure date math against the branch timezone.
   */
  const rangeFor = React.useMemo(() => {
    const cache = new Map<string, string | null>()
    return (preset: string): string | null => {
      if (!cache.has(preset)) {
        const range = resolveDerivationRange({ preset: preset as PeriodPreset, timeZone })
        cache.set(preset, range ? `${range.from.slice(0, 10)} → ${range.to.slice(0, 10)}` : null)
      }
      return cache.get(preset) ?? null
    }
  }, [timeZone])

  if (items.length === 0) return null

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button type='button' variant='ghost' size='sm' className='-ml-2 gap-2'>
          <ChevronDownIcon className={open ? "size-4 rotate-180" : "size-4"} aria-hidden='true' />
          {t("title")}
          <Badge variant='secondary'>{items.length}</Badge>
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className='pt-2'>
        <ul className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
          {items.map((item) => (
            <li
              key={item.id}
              className='bg-card flex items-start justify-between gap-2 rounded-lg border p-3'
            >
              <div className='min-w-0 space-y-1'>
                <p className='truncate text-sm font-medium'>{item.name}</p>
                {/* The criteria in one line, so a treasurer can tell two similar rules apart without opening
                    either: what is measured, over which period, at what share, and where it goes. */}
                <p className='text-muted-foreground truncate text-xs'>
                  {item.mode === TransferDerivationMode.Fund ? item.sourceFundName : item.sourceAccountName}
                  {" · "}
                  {t(`presets.${item.preset}` as never)}
                  {" · "}
                  {item.percentage}%
                </p>
                {/* The dates the period resolves to TODAY. "This week" is not self-evident, and this card is
                    one click from creating a real transfer — the operator should see the span they are about
                    to remit before pressing play, not after. */}
                {rangeFor(item.preset) ? (
                  <p className='text-muted-foreground truncate text-xs tabular-nums'>
                    {rangeFor(item.preset)}
                  </p>
                ) : null}
                <p className='text-muted-foreground truncate text-xs'>
                  {t("toDestination", {
                    destination: getBranchDisplayLabel({
                      branchType: item.destinationBranchType,
                      branchName: item.destinationBranchName,
                      parishLabel: t("parish"),
                      fallbackLabel: item.destinationBranchName,
                    }),
                  })}
                </p>
              </div>

              {/* Icon-only actions, so each carries a real tooltip AND an accessible name — a bare icon is a
                  guess, and on a destructive one a guess is expensive. */}
              <div className='flex shrink-0 items-center gap-1'>
                {canEdit ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      className='size-8'
                      aria-label={t("actions.use", { name: item.name })}
                      onClick={() => onApply(item)}
                    >
                      <PlayIcon className='size-4' aria-hidden='true' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("actions.useHint")}</TooltipContent>
                </Tooltip>
                ) : null}

                {canEdit ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type='button'
                      size='icon'
                      variant='ghost'
                      className='text-destructive hover:text-destructive size-8'
                      aria-label={t("actions.delete", { name: item.name })}
                      disabled={remove.isPending}
                      onClick={() => setPendingDelete(item)}
                    >
                      <Trash2Icon className='size-4' aria-hidden='true' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("actions.deleteHint")}</TooltipContent>
                </Tooltip>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </CollapsibleContent>

      {/* The SHARED confirm dialog (@ibada/ui) — destructive variant, so the confirm reads red like every
          other destructive confirm in the app. One instance for the list, driven by which row was clicked. */}
      <ConfirmActionDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null)
        }}
        title={t("delete.title", { name: pendingDelete?.name ?? "" })}
        description={t("delete.description")}
        confirmLabel={t("delete.confirm")}
        cancelLabel={t("delete.cancel")}
        confirmVariant='destructive'
        pending={remove.isPending}
        onConfirm={async () => {
          if (!canEdit || !pendingDelete || !workspaceId || !branchId) return
          try {
            await remove.mutateAsync({ id: pendingDelete.id, workspaceId, branchId })
            setPendingDelete(null)
          } catch (error) {
            toastError(error)
          }
        }}
      />
    </Collapsible>
  )
}
